import {
    DestroyRef, inject, Injectable
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    AuthenticationService, COMMON_APP_CONFIG, ICommonConfig, LoggingService
} from "@shopliftr/common-ng";
import { distinctUntilChanged } from "rxjs";
import { FlyerStateService } from "../../deal-entry/services/flyer-state.service";
import { TaskListStateService } from "../../task/services/task-list-state.service";

/**
 * Sends the signed-in user, the selected task and the selected flyer to New Relic so that errors,
 * page actions and slow responses can be traced back to who was doing what.
 *
 * Context is set when a source emits a value and cleared when it emits `undefined`. The user name is
 * only sent when `config.newrelic.includeUserNames` is true. It also asks the logger to report
 * unusually large deal responses.
 *
 * **Service Type:** Root service started by an app initializer
 *
 * **Provided:** Root-level singleton
 *
 * @example
 * ```typescript
 * // app.options.ts
 * additionalProviders: [provideAppInitializer(() => inject(MonitoringContextWorkflowService).start())]
 * ```
 */
@Injectable({
    providedIn: "root"
})
export class MonitoringContextWorkflowService {

    /** Responses from the deal-entry deal endpoint larger than this many bytes are reported (500 KiB). */
    private static readonly _LARGE_DEAL_RESPONSE_BYTES = 500 * 1024;

    private readonly _authService = inject(AuthenticationService);
    private readonly _config = inject<ICommonConfig>(COMMON_APP_CONFIG);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _flyerState = inject(FlyerStateService);
    private readonly _logger = inject(LoggingService);
    private readonly _taskListState = inject(TaskListStateService);

    private _started = false;

    /**
     * Starts sending monitoring context to New Relic. Safe to call more than once: only the first
     * call subscribes, later calls do nothing.
     */
    start(): void {
        if (this._started) {
            return;
        }

        this._started = true;
        this._logger.observeLargeResponses("data-entry-deal", MonitoringContextWorkflowService._LARGE_DEAL_RESPONSE_BYTES);
        this._monitorUser();
        this._monitorTask();
        this._monitorFlyer();
        this._monitorRootFlyer();
    }

    /**
     * Sets the user id and team (and optionally the user name) on sign-in, and clears them on sign-out.
     */
    private _monitorUser(): void {
        this._authService.authenticatedUser$.pipe(
            distinctUntilChanged((previous, current) => previous?.id === current?.id),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((user) => {
            if (!user) {
                this._logger.clearUser();
                this._logger.clearContext(["teamId", "teamName", "userName"]);
                return;
            }

            this._logger.setUser(user.id);
            this._logger.setContext({
                teamId: user.teamId,
                teamName: user.teamName,
                ...(this._config.newrelic?.includeUserNames ? { userName: user.name } : {})
            });
        });
    }

    /**
     * Sets the selected task id and name, and clears them when the task is deselected.
     */
    private _monitorTask(): void {
        this._taskListState.selectedTask$.pipe(
            distinctUntilChanged((previous, current) => previous?.id === current?.id),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((task) => {
            if (!task) {
                this._logger.clearContext(["taskId", "taskName"]);
                return;
            }

            this._logger.setContext({
                taskId: task.id,
                taskName: task.description
            });
        });
    }

    /**
     * Sets the selected flyer id and name, and clears them when the flyer is deselected. Independent
     * of the root flyer.
     */
    private _monitorFlyer(): void {
        this._flyerState.selectedFlyer$.pipe(
            distinctUntilChanged((previous, current) => previous?.id === current?.id),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((flyer) => {
            if (!flyer) {
                this._logger.clearContext(["flyerId", "flyerName"]);
                return;
            }

            this._logger.setContext({
                flyerId: flyer.id,
                flyerName: flyer.name
            });
        });
    }

    /**
     * Sets the selected root flyer id and its chain name, and clears them when the root flyer is
     * deselected. Independent of the selected flyer.
     */
    private _monitorRootFlyer(): void {
        this._flyerState.selectedRootFlyer$.pipe(
            distinctUntilChanged((previous, current) => previous?.id === current?.id),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((rootFlyer) => {
            if (!rootFlyer) {
                this._logger.clearContext(["rootFlyerId", "chainName"]);
                return;
            }

            this._logger.setContext({
                rootFlyerId: rootFlyer.id,
                chainName: rootFlyer.chain?.name
            });
        });
    }
}
